import { ReleaseOption } from '@models/remmi-release.model';

/**
 * Release module keys. They match `ASSISTANT_ACCESS_MODULES` plus `tasks`, `chat` and `automation`,
 * which is what the backend stores on each release. Used by the history drawer, where non-admins
 * have no `/admin/meta` to read the list from.
 */
export const RELEASE_MODULES: ReleaseOption[] = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'leads', label: 'Leads' },
  { value: 'projects', label: 'Projects' },
  { value: 'reports', label: 'Reporting' },
  { value: 'contacts', label: 'Contacts' },
  { value: 'listings', label: 'Listings' },
  { value: 'market_place', label: 'Market Place' },
  { value: 'contracts', label: 'Contracts' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'tasks', label: 'Tasks' },
  { value: 'library', label: 'Library' },
  { value: 'email', label: 'Email' },
  { value: 'chat', label: 'Chat' },
  { value: 'automation', label: 'Automation' },
  { value: 'settings', label: 'Settings' },
];

/**
 * URL → release module. Order matters: the specific project tabs come before the general project
 * rule, market-place before listings, and office-contact / workflow before the settings catch-all.
 * Project ids are matched loosely (`[^/]+`) so a non-ObjectId id never drops the page out of its module.
 */
const RELEASE_MODULE_ROUTES: { pattern: RegExp; module: string }[] = [
  { pattern: /^\/projects\/(?:edit_\/)?[^/]+\/lead(?:\/|$)/i, module: 'leads' },
  { pattern: /^\/projects\/(?:edit_\/)?[^/]+\/reporting(?:\/|$)/i, module: 'reports' },
  { pattern: /^\/projects\/[^/]+\/calendar(?:\/|$)/i, module: 'calendar' },
  { pattern: /^\/projects\/edit_\//i, module: 'projects' },
  { pattern: /^\/project\/projects(?:\/|$)/i, module: 'projects' },
  { pattern: /^\/project_\//i, module: 'projects' },
  { pattern: /^\/(?:dashboard-new)?$/i, module: 'dashboard' },
  { pattern: /^\/leads(?:\/|$)/i, module: 'leads' },
  { pattern: /^\/report(?:\/|$)/i, module: 'reports' },
  { pattern: /^\/setting\/office-contact(?:\/|$)/i, module: 'contacts' },
  { pattern: /^\/listings\/market-place(?:\/|$)/i, module: 'market_place' },
  { pattern: /^\/(?:listings|property|ofi)(?:\/|$)/i, module: 'listings' },
  { pattern: /^\/contracts(?:\/|$)/i, module: 'contracts' },
  { pattern: /^\/(?:calender|my-calander)(?:\/|$)/i, module: 'calendar' },
  { pattern: /^\/tasks(?:\/|$)/i, module: 'tasks' },
  { pattern: /^\/(?:library(?:\/|$)|files$)/i, module: 'library' },
  { pattern: /^\/(?:my-inbox|email-inbox)(?:\/|$)/i, module: 'email' },
  { pattern: /^\/chat(?:\/|$)/i, module: 'chat' },
  { pattern: /^\/setting\/workflow(?:\/|$)/i, module: 'automation' },
  { pattern: /^\/setting(?:\/|$)/i, module: 'settings' },
];

/** The release module a URL belongs to, or `null` when that page gets no banner. Query, matrix params and fragment are ignored. */
export function releaseModuleForUrl(url: string): string | null {
  const path = (url ?? '').split(/[?#;]/)[0] || '/';
  return RELEASE_MODULE_ROUTES.find(route => route.pattern.test(path))?.module ?? null;
}

export function releaseModuleLabel(key: string): string {
  return RELEASE_MODULES.find(module => module.value === key)?.label ?? key;
}
